"use client";

import {
  CodeBlock,
  CodeBlockCopyButton,
} from "@repo/ui/ai-elements/code-block";
import { SqlCodeBlock } from "@repo/ui/components/sql-code-block";
import { cn } from "@repo/ui/lib/utils";
import { cloneElement, isValidElement, type ReactNode } from "react";
import type { BundledLanguage } from "shiki";
import { bundledLanguages } from "shiki/langs";
import type { Components as StreamdownComponents } from "streamdown";

/** Recursively flatten a node's text children into a single string. Used to
 * recover the raw source of a fenced code block from its rendered children. */
export const flattenToString = (node: ReactNode): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flattenToString).join("");
  if (isValidElement(node)) {
    return flattenToString(
      (node.props as { children?: ReactNode } | null)?.children,
    );
  }
  return "";
};

/**
 * App-owned Streamdown component overrides. Tables and fenced code blocks are
 * rendered with our own components (the shared {@link CodeBlock} for code) so
 * they don't depend on Streamdown's internal markup / data attributes. Inline
 * code uses Streamdown's dedicated `inlineCode` slot.
 */
export const markdownComponents: StreamdownComponents = {
  // Render the code child directly (no <pre>, since our block code is a <div>
  // that can't legally nest in <pre>), but preserve the `data-block` marker
  // Streamdown's default `pre` adds — its `code` dispatcher uses it to route
  // fenced blocks to `code` and everything else to `inlineCode`.
  pre: ({ children }) =>
    isValidElement<{ "data-block"?: string }>(children)
      ? cloneElement(children, { "data-block": "true" })
      : children,
  code: ({ className, children }) => {
    const raw = /language-(\w+)/.exec(className ?? "")?.[1];
    const code = flattenToString(children).replace(/\n$/, "");
    if (raw === "sql") {
      return <SqlCodeBlock code={code} className="my-3 border-border" />;
    }
    const language =
      raw && raw in bundledLanguages
        ? (raw as BundledLanguage)
        : ("text" as BundledLanguage);
    return (
      <CodeBlock code={code} language={language} className="my-3 border-border">
        <CodeBlockCopyButton className="opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100" />
      </CodeBlock>
    );
  },
  inlineCode: ({ children, node: _node, ...props }) => (
    <code
      className="rounded border bg-muted/40 px-1 py-px font-mono text-[0.92em] text-orange-600 dark:text-orange-400"
      {...props}
    >
      {children}
    </code>
  ),
  table: ({ className, children, node: _node, ...props }) => (
    <div className="my-3 max-h-96 overflow-auto rounded-md border border-border">
      <table
        className={cn("w-full border-collapse text-xs", className)}
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  thead: ({ node: _node, ...props }) => (
    <thead className="sticky top-0 z-10 bg-background" {...props} />
  ),
  tbody: ({ node: _node, ...props }) => <tbody {...props} />,
  tr: ({ node: _node, ...props }) => (
    <tr className="border-border border-b last:border-0" {...props} />
  ),
  th: ({ node: _node, ...props }) => (
    <th
      className="px-3 py-2 text-left font-medium text-muted-foreground"
      {...props}
    />
  ),
  td: ({ node: _node, ...props }) => (
    <td className="px-3 py-2 align-top" {...props} />
  ),
};
